// src/SuperKpis.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';
import {
  BarChart3,
  CheckCircle,
  Clock,
  Car,
  ShieldCheck,
  FileSignature,
  MessageCircle,
  RefreshCw,
  Building2,
  Calendar,
} from 'lucide-react';

function fmtPct(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toFixed(2)}%`;
}
function fmtNum(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${Number(v)}`;
}
function fmtMin(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)} min`;
}
function fmtHours(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)} h`;
}

function Card({ title, icon: Icon, value, sub, tone = 'sky' }) {
  const toneMap = {
    sky: 'border-sky-200 bg-white',
    emerald: 'border-emerald-200 bg-white',
    amber: 'border-amber-200 bg-white',
    violet: 'border-violet-200 bg-white',
    slate: 'border-slate-200 bg-white',
  };

  return (
    <div className={`rounded-3xl border ${toneMap[tone] || toneMap.sky} shadow-sm p-5`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium text-slate-600">{title}</div>
        {Icon ? (
          <div className="h-9 w-9 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center">
            <Icon className="h-5 w-5 text-slate-700" />
          </div>
        ) : null}
      </div>
      <div className="mt-2 text-3xl font-semibold text-slate-900">{value}</div>
      {sub ? <div className="mt-1 text-sm text-slate-600">{sub}</div> : null}
    </div>
  );
}

export default function SuperKpis() {
  const today = useMemo(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);

  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);

  const [orgId, setOrgId] = useState(() => {
    return localStorage.getItem('active_org_id') || '1';
  });
  
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(today);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);

  async function load() {
    try {
      setLoading(true);
      setErr('');

      const params = { start, end };
      if (orgId) params.organization_id = orgId;

      const res = await API.get('/admin/kpis', { params });
      setData(res.data || null);
    } catch (e) {
      setData(null);
      setErr(e?.response?.data?.error || e?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const opsByDay = Array.isArray(data?.ops_load_by_day) ? data.ops_load_by_day : [];

  return (
    <div className="p-4 sm:p-6">
      {/* Hero */}
      <div className="rounded-3xl bg-gradient-to-br from-sky-600 via-sky-500 to-indigo-600 text-white p-6 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-2 text-white/90 text-sm">
              <BarChart3 className="h-4 w-4" />
              <span>Super Admin</span>
              <span className="text-white/60">/</span>
              <span>KPI Dashboard</span>
            </div>
            <h1 className="mt-2 text-2xl sm:text-3xl font-semibold">Operational ROI Metrics</h1>
            <p className="mt-1 text-white/90 text-sm">
              Track kept visits, parent confirmations, substitutions speed, drive time, EVV, G-Sign and ops load.
            </p>
          </div>

          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-2xl bg-white/15 hover:bg-white/20 border border-white/20 px-4 py-2 text-sm font-medium disabled:opacity-60"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Controls */}
        <div className="mt-5 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="rounded-2xl bg-white/10 border border-white/15 p-3">
            <label className="text-xs text-white/80 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Organization ID (required for super_admin)
            </label>
            <input
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              placeholder="e.g. 1"
              className="mt-2 w-full rounded-xl bg-white/90 text-slate-900 placeholder:text-slate-500 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white/60"
            />
          </div>

          <div className="rounded-2xl bg-white/10 border border-white/15 p-3">
            <label className="text-xs text-white/80 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Start
            </label>
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-2 w-full rounded-xl bg-white/90 text-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white/60"
            />
          </div>

          <div className="rounded-2xl bg-white/10 border border-white/15 p-3">
            <label className="text-xs text-white/80 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              End
            </label>
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-2 w-full rounded-xl bg-white/90 text-slate-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-white/60"
            />
          </div>

          <div className="rounded-2xl bg-white/10 border border-white/15 p-3 flex items-end">
            <button
              onClick={load}
              disabled={loading} // super_admin should set orgId; keep strict here
              className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-slate-900 hover:bg-white/90 px-4 py-2 text-sm font-semibold disabled:opacity-60"
            >
              <BarChart3 className="h-4 w-4" />
              Load KPIs
            </button>
          </div>
        </div>
      </div>

      {err ? (
        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700">
          {err}
        </div>
      ) : null}

      {/* KPI Cards */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card
          title="Kept Visit Rate"
          icon={CheckCircle}
          value={fmtPct(data?.kept_rate_pct)}
          sub={`${fmtNum(data?.kept_visits)} kept / ${fmtNum(data?.scheduled_visits)} scheduled`}
          tone="emerald"
        />
        <Card
          title="Parent Accept Rate"
          icon={MessageCircle}
          value={fmtPct(data?.accept_rate_pct)}
          sub={`${fmtNum(data?.visits_accepted)} accepted / ${fmtNum(data?.visits_notified)} notified`}
          tone="sky"
        />
        <Card
          title="Late Cancel Rate (under 12h)"
          icon={Clock}
          value={fmtPct(data?.parent_late_cancel_pct)}
          sub={`${fmtNum(data?.parent_late_cancels_under_12h)} late / ${fmtNum(data?.parent_cancels_total)} parent cancels`}
          tone="amber"
        />
        <Card
          title="Time-to-Cover (avg)"
          icon={Clock}
          value={data?.avg_minutes_to_cover == null ? '—' : fmtMin(data.avg_minutes_to_cover)}
          sub={data?.max_minutes_to_cover == null ? 'Needs audit action: visit_reassign_pending' : `Max: ${fmtMin(data.max_minutes_to_cover)}`}
          tone="violet"
        />

        <Card
          title="Drive min / kept visit"
          icon={Car}
          value={data?.drive_min_per_kept_visit == null ? '—' : `${Number(data.drive_min_per_kept_visit).toFixed(2)} min`}
          sub={`Total: ${fmtHours(data?.drive_hours)} (${fmtNum(data?.kept_visits_with_timelog)} visits with route)`}
          tone="slate"
        />
        <Card
          title="EVV Completion Rate"
          icon={ShieldCheck}
          value={fmtPct(data?.evv_completion_pct)}
          sub={`${fmtNum(data?.evv_complete_visits)} complete / ${fmtNum(data?.evv_required_visits)} required (exceptions: ${fmtNum(data?.evv_exceptions)})`}
          tone="sky"
        />
        <Card
          title="EVV Strict GPS OK"
          icon={ShieldCheck}
          value={fmtPct(data?.evv_strict_gps_ok_pct)}
          sub="Has check-in/out time + GPS lat/lon present"
          tone="emerald"
        />
        <Card
          title="G-Sign Confirmation Rate"
          icon={FileSignature}
          value={fmtPct(data?.gsign_rate_pct)}
          sub={`${fmtNum(data?.gsign_confirmed_visits)} confirmed / ${fmtNum(data?.gsign_total_visits)} visits`}
          tone="slate"
        />
      </div>

      {/* Ops Load */}
      <div className="mt-6 rounded-3xl border border-slate-200 bg-white shadow-sm">
        <div className="p-5 flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-slate-900">Ops Load</div>
            <div className="text-sm text-slate-600">
              Proxy for scheduling “chaos”: number of key operational actions in audit log.
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-semibold text-slate-900">{fmtNum(data?.ops_load_events_total)}</div>
            <div className="text-xs text-slate-500">events in range</div>
          </div>
        </div>

        <div className="px-5 pb-5">
          {opsByDay.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-slate-600 text-sm">
              No ops-load breakdown found (or no matching audit actions in this range).
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold text-slate-700">Day</th>
                    <th className="text-left px-3 py-2 font-semibold text-slate-700">Events</th>
                  </tr>
                </thead>
                <tbody>
                  {opsByDay.map((r, idx) => (
                    <tr key={idx} className="border-t border-slate-200">
                      <td className="px-3 py-2 text-slate-700">{r.day}</td>
                      <td className="px-3 py-2 text-slate-900 font-medium">{r.events}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-3 text-xs text-slate-500">
            Actions included: visit_create, visit_update, visit_cancel, visit_reassign_*, visit_evv_edit, visit_notes_update
          </div>
        </div>
      </div>
    </div>
  );
}

